import {
  FacetedFilter,
  type FacetedFilterOption,
} from "@/components/feature/KnowledgebaseFilters"
import { Button } from "@/components/ui/button"
import { useTranslation } from "@/hooks/useTranslation/useTranslation"
import { Filter, X } from "lucide-react"
import { useState } from "react"

export interface MasterDataFacet {
  key: string
  title: string
  options: FacetedFilterOption[]
  selected: string[]
  onSelectionChange: (values: string[]) => void
  /** Server-backed search: when set, typing calls this instead of filtering `options` locally. */
  onQueryChange?: (query: string) => void
  hasMore?: boolean
  onLoadMore?: () => void
  isLoadingMore?: boolean
}

/**
 * Open/active-filter state shared between the filter toggle button (row 1)
 * and the faceted chips row (row 2) — lifted out so the two can be rendered
 * in different rows of the list header while staying in sync.
 */
export function useMasterDataFiltersState(facets: MasterDataFacet[]) {
  const hasActiveFilters = facets.some((facet) => facet.selected.length > 0)
  const [isOpen, setIsOpen] = useState(hasActiveFilters)

  // Auto-open exactly once, on the transition into having active filters —
  // NOT "always open while filters are active", since a manual close should
  // still stick as long as `hasActiveFilters` doesn't flip again. Adjusted
  // during render (React's documented pattern) instead of a useEffect so
  // there's no extra post-commit render pass.
  const [prevHasActiveFilters, setPrevHasActiveFilters] =
    useState(hasActiveFilters)
  if (hasActiveFilters !== prevHasActiveFilters) {
    setPrevHasActiveFilters(hasActiveFilters)
    if (hasActiveFilters) setIsOpen(true)
  }

  return { isOpen, setIsOpen, hasActiveFilters }
}

/** Filter toggle button — same size/shape as the sort button beside it. */
export function MasterDataFilterToggle({
  isOpen,
  onToggle,
  hasActiveFilters,
  agentTarget,
}: {
  isOpen: boolean
  onToggle: () => void
  hasActiveFilters: boolean
  /** Agent-addressable target key (see `agentTargets.ts`), marked on the button. */
  agentTarget?: string
}) {
  const { translate } = useTranslation()
  return (
    <Button
      variant="outline"
      size="icon"
      className="relative size-8 shrink-0 border-grey4 bg-grey3 text-grey6 hover:text-white data-[active=true]:bg-grey4"
      data-active={isOpen}
      data-agent-target={agentTarget}
      aria-label={translate("MC_MD_FILTER_BTN")}
      title={translate("MC_MD_FILTER_BTN")}
      onClick={onToggle}
    >
      <Filter className="size-4" />
      {hasActiveFilters && (
        <span className="absolute -top-0.5 -right-0.5 size-2 rounded-full bg-yellow1" />
      )}
    </Button>
  )
}

// Radix marks the trigger data-state="open" while its dropdown shows.
const OPEN_CHIP_CLASS =
  "data-[state=open]:border-solid data-[state=open]:bg-grey4"

/** Faceted filter chips + clear-all — the row shown under the search bar while open. */
export function MasterDataFilterChips({
  facets,
  hasActiveFilters,
  onClearAll,
}: {
  facets: MasterDataFacet[]
  hasActiveFilters: boolean
  onClearAll: () => void
}) {
  const { translate } = useTranslation()
  if (facets.length === 0) return null

  return (
    <div className="flex flex-wrap items-center gap-2">
      {facets.map((facet) => (
        <FacetedFilter
          key={facet.key}
          title={facet.title}
          options={facet.options}
          selected={facet.selected}
          onSelectionChange={facet.onSelectionChange}
          onQueryChange={facet.onQueryChange}
          hasMore={facet.hasMore}
          onLoadMore={facet.onLoadMore}
          isLoadingMore={facet.isLoadingMore}
          hideLeadingIcon
          compactCount
          // The chip whose dropdown is open stands out, so it is clear which
          // filter the list below belongs to.
          triggerClassName={OPEN_CHIP_CLASS}
        />
      ))}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="icon"
          className="size-8 shrink-0 text-grey5 hover:text-white"
          aria-label={translate("MC_MD_FILTER_CLEAR_ALL")}
          title={translate("MC_MD_FILTER_CLEAR_ALL")}
          onClick={onClearAll}
        >
          <X className="size-4" />
        </Button>
      )}
    </div>
  )
}
